import { StorageKeys, StorageUtilities } from '@dealstack/frontend/shared/helper/storageUtils';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DEFAULT_LANGUAGE } from '@dealstack/frontend/shared/mechanism/i18n/i18nLayer';
import { Languages } from '@dealstack/shared/lib/common/i18n/i18n';
import { Menu } from '@mantine/core';
import React, { memo, useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line no-restricted-imports
import { useTranslation } from 'react-i18next';
import { MantineMenu } from '@dealstack/frontend/shared/components/MantineMenu/MantineMenu';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import ReactCountryFlag from 'react-country-flag';
import { GENERIC_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';

const LANGUAGE_COUNTRY_CODES: Record<Languages, string> = {
	[Languages.DA]: 'DK',
	[Languages.EN]: 'GB',
	[Languages.IT]: 'IT',
	[Languages.FI]: 'FI',
	[Languages.FR]: 'FR',
	[Languages.DE]: 'DE',
	[Languages.NO]: 'NO',
	[Languages.ES]: 'ES',
};

const LANGUAGE_TRANSLATION_KEYS: Record<Languages, string> = {
	[Languages.DA]: 'danish',
	[Languages.EN]: 'english',
	[Languages.IT]: 'italian',
	[Languages.FI]: 'finnish',
	[Languages.FR]: 'french',
	[Languages.DE]: 'german',
	[Languages.NO]: 'norwegian',
	[Languages.ES]: 'spanish',
};

interface ILanguagesList {
	value: Languages;
	countryCode: (typeof LANGUAGE_COUNTRY_CODES)[Languages];
}

const CountryFlag = ({ countryCode }: { countryCode?: ILanguagesList['countryCode'] }) =>
	countryCode ? (
		<ReactCountryFlag
			countryCode={countryCode}
			svg
			style={{
				width: '1em',
				height: '1em',
			}}
			title={countryCode}
		/>
	) : null;

const makeFullLanguageList = (): ILanguagesList[] => {
	return Object.values(Languages).map((language) => ({
		value: language,
		countryCode: LANGUAGE_COUNTRY_CODES[language],
	}));
};

const getLanguageOption = (current: string, allLanguages: ILanguagesList[]) =>
	allLanguages.find((l) => l.value === current);
const getEnabledLanguages = (languages: string[] = [], allLanguages: ILanguagesList[]) =>
	allLanguages.filter((l) => languages.includes(l.value));

const LanguagePicker = () => {
	const tCommonUi = useTranslationCommonUi();
	const { i18n } = useTranslation();

	const allLanguages = makeFullLanguageList();

	const [languageLists, setLanguageLists] = useState<ILanguagesList[]>([]);
	const [selected, setSelected] = useState(() =>
		getLanguageOption(i18n.resolvedLanguage ?? i18n.language, allLanguages)
	);

	useEffect(() => {
		const languagesConfig = StorageUtilities.getItem<string[]>(
			StorageKeys.UI_LANGUAGE_LIST
		) || [DEFAULT_LANGUAGE];

		const trimmedLanguagesConfig = languagesConfig.map((lang) => lang.trim());
		setLanguageLists(getEnabledLanguages(trimmedLanguagesConfig, allLanguages));
	}, []);

	const changeLanguage = (language: ILanguagesList) => {
		const previous = selected;

		void i18n.changeLanguage(language.value, (error) => {
			if (!error) {
				setSelected(language);

				return;
			}

			void i18n.changeLanguage(previous?.value ?? DEFAULT_LANGUAGE);
			setSelected(previous);

			showToastV2({ title: tCommonUi('language.change_failed'), state: 'error' });
		});
	};

	const items = useMemo(
		() =>
			languageLists.map((language) => ({
				key: language.value,
				label: tCommonUi(
					`language.language_options.${LANGUAGE_TRANSLATION_KEYS[language.value]}`
				),
				selected: selected?.value === language.value,
				selectedGreyTheme: true,
				leftSection: <CountryFlag countryCode={language.countryCode} />,
				onClick: () => changeLanguage(language),
			})),
		[selected, languageLists, tCommonUi]
	);

	return (
		<MantineMenu
			dataTestId={GENERIC_ELEMENT_ID.LANGUAGE_PICKER}
			position="bottom-end"
			hideArrow={true}
			capitalizeMenuItem
			targetContent={<CountryFlag countryCode={selected?.countryCode} />}
			dropdownTopContent={
				<Menu.Label px="0.5rem" c="gray.9" fw={600}>
					{tCommonUi('language.header')}
				</Menu.Label>
			}
			items={items}
		/>
	);
};

const Memoized = memo(LanguagePicker);

export { Memoized as LanguagePicker };
